// Подписка на Web Push через client-transport. SPEC pwa-shell §4.7 / §4.8 / §4.9.
//
// Флоу: requestPermission (по жесту) → PushPort.getVapidKey → base64url→Uint8Array →
// pushManager.subscribe(userVisibleOnly) → PushPort.subscribe(sub.toJSON()). unsubscribe снимает
// ОБЕ стороны (сервер + локально). pushsubscriptionchange пере-подписывает тем же VAPID-ключом.
//
// Изоляция: `pushManager`/окружение инъецируются (порты ниже) — логика тестируется в Node без
// браузера. Обмен с сервером — строго через PushPort (= client-transport), в обход не лезем.
/**
 * base64url (VAPID public key) → Uint8Array (`applicationServerKey`). Чистая функция.
 * VAPID-ключ приходит base64url (RFC 4648 §5): '-'→'+', '_'→'/', добивка '='. Браузерный
 * `pushManager.subscribe` требует именно BufferSource, а не строку.
 */
export function urlBase64ToUint8Array(base64Url) {
    const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
    const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atobUniversal(base64);
    const output = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i += 1) {
        output[i] = raw.charCodeAt(i);
    }
    return output;
}
/** atob — глобальный и в браузере/SW, и в Node 16+. */
function atobUniversal(b64) {
    return atob(b64);
}
/**
 * Гейт предложения подписки (чистая функция, инв.4). Правила §4.8:
 *   - нет PushManager/SW → 'unsupported' (старый браузер) — UI прячет пункт;
 *   - iOS НЕ в standalone → 'ios-needs-install' — показать «Поделиться → На экран Домой»,
 *     НЕ предлагать подписку (на iOS push доступен лишь из установленного PWA, 16.4+);
 *   - permission==='denied' → 'denied' — не доканывать, показать «включите в браузере»;
 *   - иначе → можно предлагать (разрешение запросим по жесту в subscribeToPush).
 */
export function canOfferPush(env) {
    if (!env.pushSupported)
        return { canOffer: false, reason: 'unsupported' };
    if (env.isIOS && !env.isStandalone)
        return { canOffer: false, reason: 'ios-needs-install' };
    if (env.permission === 'denied')
        return { canOffer: false, reason: 'denied' };
    return { canOffer: true };
}
/**
 * Полный subscribe-флоу (§4.7). Порядок строгий и проверяется тестом:
 *   1. гейт canOfferPush (инв.4); если нельзя — выходим с причиной (без побочных эффектов);
 *   2. если есть активная подписка с тем же endpoint — НЕ подписываемся в SW повторно
 *      (идемпотентность §4.7 п.6), но переотправляем на сервер (дешёвый upsert надёжности);
 *   3. иначе requestPermission по жесту; 'denied'/'default' → выход без подписки (§4.9);
 *   4. getVapidKey → base64url→Uint8Array → pushManager.subscribe(userVisibleOnly:true);
 *   5. transport.subscribe(sub.toJSON()) — регистрация на сервере.
 */
export async function subscribeToPush(deps) {
    const offer = canOfferPush(deps.env);
    if (!offer.canOffer)
        return { ok: false, reason: offer.reason };
    // (2) идемпотентность: активная подписка уже есть → upsert на сервер, без повторного subscribe.
    const existing = await deps.pushManager.getSubscription();
    if (existing !== null) {
        await deps.transport.subscribe(toDTO(existing.toJSON(), existing.endpoint));
        return { ok: true, endpoint: existing.endpoint, reused: true };
    }
    // (3) разрешение строго по жесту (env.permission мог быть 'default').
    if (deps.env.permission !== 'granted') {
        const permission = await deps.requestPermission();
        if (permission !== 'granted') {
            return { ok: false, reason: permission === 'denied' ? 'denied' : 'dismissed' };
        }
    }
    // (4) VAPID → ключ → подписка в pushManager (трогает ТОЛЬКО pwa-shell, §4.7).
    const vapid = await deps.transport.getVapidKey();
    const applicationServerKey = urlBase64ToUint8Array(vapid);
    const sub = await deps.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
    // (5) регистрация на сервере через client-transport (W3C PushSubscriptionJSON).
    await deps.transport.subscribe(toDTO(sub.toJSON(), sub.endpoint));
    return { ok: true, endpoint: sub.endpoint, reused: false };
}
/**
 * Отписка (§4.7 п.5): снимаем И на сервере, И локально. Порядок: сначала сервер
 * (transport.unsubscribe(endpoint)), потом локальный sub.unsubscribe() — чтобы при сбое второго
 * сервер уже не слал на мёртвую подписку. Нет активной подписки → no-op (идемпотентно).
 */
export async function unsubscribeFromPush(deps) {
    const sub = await deps.pushManager.getSubscription();
    if (sub === null)
        return false;
    await deps.transport.unsubscribe(sub.endpoint);
    await sub.unsubscribe();
    return true;
}
/**
 * Пере-подписка при ротации браузером (`pushsubscriptionchange`, §4.9). Подписываемся тем же
 * VAPID-ключом и отправляем новую подписку на сервер — иначе пуши тихо умирают. Без жеста/
 * разрешения (браузер инициировал ротацию сам).
 */
export async function resubscribeOnChange(deps) {
    const vapid = await deps.transport.getVapidKey();
    const applicationServerKey = urlBase64ToUint8Array(vapid);
    const sub = await deps.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
    await deps.transport.subscribe(toDTO(sub.toJSON(), sub.endpoint));
    return sub.endpoint;
}
/** Приводит W3C PushSubscriptionJSON к нашему PushSubscriptionDTO (shared-contracts). */
function toDTO(json, endpoint) {
    return {
        endpoint: json.endpoint ?? endpoint,
        keys: {
            p256dh: json.keys?.p256dh ?? '',
            auth: json.keys?.auth ?? '',
        },
    };
}
//# sourceMappingURL=push-subscription.js.map